"use client";

import { c as _c } from "react/compiler-runtime";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { CreateIssueButton } from "@/components/create-issue-button";
import { CertificatesSection } from "@/components/domain/certificates/certificates-section";
import { CertificatesSectionSkeleton } from "@/components/domain/certificates/certificates-section-skeleton";
import { DnsSection } from "@/components/domain/dns/dns-section";
import { DnsSectionSkeleton } from "@/components/domain/dns/dns-section-skeleton";
import { HeadersSection } from "@/components/domain/headers/headers-section";
import { HeadersSectionSkeleton } from "@/components/domain/headers/headers-section-skeleton";
import { HostingSection } from "@/components/domain/hosting/hosting-section";
import { HostingSectionSkeleton } from "@/components/domain/hosting/hosting-section-skeleton";
import { RegistrationSection } from "@/components/domain/registration/registration-section";
import { RegistrationSectionSkeleton } from "@/components/domain/registration/registration-section-skeleton";
import { DomainReportHeader } from "@/components/domain/report-header";
import { SectionNav } from "@/components/domain/report-nav";
import { SectionErrorBoundary } from "@/components/domain/report-section-error-boundary";
import { SectionFailedAlert } from "@/components/domain/section-failed-alert";
import { SeoSection } from "@/components/domain/seo/seo-section";
import { SeoSectionSkeleton } from "@/components/domain/seo/seo-section-skeleton";
import { DomainUnregisteredCard } from "@/components/domain/unregistered-card";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSectionTracking } from "@/hooks/use-section-tracking";
import { analytics } from "@/lib/analytics/client";
import { HEADER_HEIGHT, SCROLL_PADDING, SECTION_NAV_HEIGHT } from "@/lib/constants/layout";
import { sections } from "@/lib/constants/sections";
import { useSearchHistoryStore } from "@/lib/stores/search-history-store";
import { useTRPC } from "@/lib/trpc/client";
import { Button } from "@domainstack/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@domainstack/ui/empty";
function resolveScrollMargin(isMobile: boolean) {
  const headerHeight = !isMobile ? HEADER_HEIGHT : 0;
  return headerHeight + SECTION_NAV_HEIGHT + SCROLL_PADDING;
}
function AllSkeletonsExceptRegistration() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><HostingSectionSkeleton /><DnsSectionSkeleton /><CertificatesSectionSkeleton /><HeadersSectionSkeleton /><SeoSectionSkeleton /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const staticQueryOptions = {
  staleTime: Number.POSITIVE_INFINITY,
  retry: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false
} as const;
const REGISTRATION_LOOKUP_MESSAGES: Record<string, string> = {
  unsupported_tld: "This TLD is not supported for registration lookups.",
  timeout: "The registration lookup timed out. Please try again.",
  retry: "The registration lookup timed out. Please try again.",
  lookup_failed: "We couldn't fetch registration data for this domain. Please try again."
};
function registrationLookupMessage(error?: string): string {
  if (error && error in REGISTRATION_LOOKUP_MESSAGES) {
    return REGISTRATION_LOOKUP_MESSAGES[error];
  }
  return "We couldn't fetch registration data for this domain. Please try again.";
}
function SuspendedHostingSection(t0) {
  const $ = _c(8);
  const {
    domain
  } = t0;
  const trpc = useTRPC();
  let t1;
  if ($[0] !== domain || $[1] !== trpc.domain.getHosting) {
    t1 = trpc.domain.getHosting.queryOptions({
      domain
    }, staticQueryOptions);
    $[0] = domain;
    $[1] = trpc.domain.getHosting;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data
  } = useSuspenseQuery(t1);
  if (!data.success) {
    let t2;
    if ($[3] !== data.error) {
      t2 = <SectionFailedAlert section={sections.hosting} error={data.error} />;
      $[3] = data.error;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== data.data || $[6] !== domain) {
    t2 = <HostingSection domain={domain} data={data.data} />;
    $[5] = data.data;
    $[6] = domain;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SuspendedDnsSection(t0) {
  const $ = _c(8);
  const {
    domain
  } = t0;
  const trpc = useTRPC();
  let t1;
  if ($[0] !== domain || $[1] !== trpc.domain.getDnsRecords) {
    t1 = trpc.domain.getDnsRecords.queryOptions({
      domain
    }, staticQueryOptions);
    $[0] = domain;
    $[1] = trpc.domain.getDnsRecords;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data
  } = useSuspenseQuery(t1);
  if (!data.success) {
    let t2;
    if ($[3] !== data.error) {
      t2 = <SectionFailedAlert section={sections.dns} error={data.error} />;
      $[3] = data.error;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== data.data || $[6] !== domain) {
    t2 = <DnsSection domain={domain} data={data.data} />;
    $[5] = data.data;
    $[6] = domain;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SuspendedCertificatesSection(t0) {
  const $ = _c(8);
  const {
    domain
  } = t0;
  const trpc = useTRPC();
  let t1;
  if ($[0] !== domain || $[1] !== trpc.domain.getCertificates) {
    t1 = trpc.domain.getCertificates.queryOptions({
      domain
    }, staticQueryOptions);
    $[0] = domain;
    $[1] = trpc.domain.getCertificates;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data
  } = useSuspenseQuery(t1);
  if (!data.success) {
    let t2;
    if ($[3] !== data.error) {
      t2 = <SectionFailedAlert section={sections.certificates} error={data.error} />;
      $[3] = data.error;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== data.data || $[6] !== domain) {
    t2 = <CertificatesSection domain={domain} data={data.data} />;
    $[5] = data.data;
    $[6] = domain;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SuspendedHeadersSection(t0) {
  const $ = _c(8);
  const {
    domain
  } = t0;
  const trpc = useTRPC();
  let t1;
  if ($[0] !== domain || $[1] !== trpc.domain.getHeaders) {
    t1 = trpc.domain.getHeaders.queryOptions({
      domain
    }, staticQueryOptions);
    $[0] = domain;
    $[1] = trpc.domain.getHeaders;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data
  } = useSuspenseQuery(t1);
  if (!data.success) {
    let t2;
    if ($[3] !== data.error) {
      t2 = <SectionFailedAlert section={sections.headers} error={data.error} />;
      $[3] = data.error;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== data.data || $[6] !== domain) {
    t2 = <HeadersSection domain={domain} data={data.data} />;
    $[5] = data.data;
    $[6] = domain;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SuspendedSeoSection(t0) {
  const $ = _c(8);
  const {
    domain
  } = t0;
  const trpc = useTRPC();
  let t1;
  if ($[0] !== domain || $[1] !== trpc.domain.getSeo) {
    t1 = trpc.domain.getSeo.queryOptions({
      domain
    }, staticQueryOptions);
    $[0] = domain;
    $[1] = trpc.domain.getSeo;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data
  } = useSuspenseQuery(t1);
  if (!data.success) {
    let t2;
    if ($[3] !== data.error) {
      t2 = <SectionFailedAlert section={sections.seo} error={data.error} />;
      $[3] = data.error;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== data.data || $[6] !== domain) {
    t2 = <SeoSection domain={domain} data={data.data} />;
    $[5] = data.data;
    $[6] = domain;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function useDomainReportTracking(domain, isRegistered) {
  const $ = _c(20);
  const addDomainToHistory = useSearchHistoryStore(_temp);
  let t0;
  let t1;
  if ($[0] !== addDomainToHistory || $[1] !== domain || $[2] !== isRegistered) {
    t0 = () => {
      if (isRegistered) {
        addDomainToHistory(domain);
      }
    };
    t1 = [isRegistered, domain, addDomainToHistory];
    $[0] = addDomainToHistory;
    $[1] = domain;
    $[2] = isRegistered;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  const viewedDomainRef = useRef(null);
  let t2;
  let t3;
  if ($[5] !== domain || $[6] !== isRegistered) {
    t2 = () => {
      if (!isRegistered || viewedDomainRef.current === domain) {
        return;
      }
      viewedDomainRef.current = domain;
      analytics.track("report_viewed", {
        domain
      });
    };
    t3 = [domain, isRegistered];
    $[5] = domain;
    $[6] = isRegistered;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  const headerRef = useRef(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const isMobile = useIsMobile();
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = Object.keys(sections);
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isMobile) {
    t5 = resolveScrollMargin(isMobile);
    $[10] = isMobile;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = {
      sectionIds: t4,
      scrollMarginPx: t5
    };
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    activeSection,
    scrollToSection
  } = useSectionTracking(t6);
  let t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const headerElement = headerRef.current;
      if (!headerElement) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        setIsHeaderVisible(entries[0].isIntersecting);
      }, {
        threshold: 0,
        rootMargin: "-10px 0px 0px 0px"
      });
      observer.observe(headerElement);
      return () => observer.disconnect();
    };
    t8 = [];
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  let t9;
  if ($[16] !== activeSection || $[17] !== isHeaderVisible || $[18] !== scrollToSection) {
    t9 = {
      headerRef,
      isHeaderVisible,
      activeSection,
      scrollToSection
    };
    $[16] = activeSection;
    $[17] = isHeaderVisible;
    $[18] = scrollToSection;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
function _temp(s) {
  return s.addDomain;
}
function getReportErrorDescription({
  isRegistrationError,
  registrationError,
  lookupFailed,
  registration
}: {
  isRegistrationError: boolean;
  registrationError: unknown;
  lookupFailed: boolean;
  registration: {
    success: false;
    error?: string;
  } | {
    success: true;
  } | null | undefined;
}) {
  if (process.env.NODE_ENV === "development" && isRegistrationError && registrationError instanceof Error) {
    return registrationError.message;
  }
  if (lookupFailed && registration && !registration.success) {
    return registrationLookupMessage(registration.error);
  }
  return "We couldn't fetch registration data for this domain. Please try again.";
}
function DomainReportLoadError(t0) {
  const $ = _c(19);
  const {
    domain,
    registrationError,
    description
  } = t0;
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <EmptyMedia variant="icon"><IconAlertTriangle /></EmptyMedia>;
    t2 = <EmptyTitle>Failed to load domain report</EmptyTitle>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== description) {
    t3 = <EmptyHeader>{t1}{t2}<EmptyDescription>{description}</EmptyDescription></EmptyHeader>;
    $[2] = description;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== domain || $[5] !== queryClient || $[6] !== trpc.domain.getRegistration) {
    t4 = () => queryClient.invalidateQueries(trpc.domain.getRegistration.queryFilter({
      domain
    }));
    $[4] = domain;
    $[5] = queryClient;
    $[6] = trpc.domain.getRegistration;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IconRefresh />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4) {
    t6 = <Button size="sm" onClick={t4}>{t5}Retry</Button>;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = registrationError instanceof Error ? registrationError : undefined;
  let t8;
  if ($[11] !== t7) {
    t8 = <CreateIssueButton error={t7} variant="outline" size="sm" />;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t6 || $[14] !== t8) {
    t9 = <EmptyContent><div className="flex flex-wrap items-center justify-center gap-2">{t6}{t8}</div></EmptyContent>;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t3 || $[17] !== t9) {
    t10 = <Empty className="border border-dashed">{t3}{t9}</Empty>;
    $[16] = t3;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function RegisteredReportSections(t0) {
  const $ = _c(21);
  const {
    domain
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <HostingSectionSkeleton />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== domain) {
    t2 = <SectionErrorBoundary sectionName="Hosting"><Suspense fallback={t1}><SuspendedHostingSection domain={domain} /></Suspense></SectionErrorBoundary>;
    $[1] = domain;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DnsSectionSkeleton />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== domain) {
    t4 = <SectionErrorBoundary sectionName="DNS"><Suspense fallback={t3}><SuspendedDnsSection domain={domain} /></Suspense></SectionErrorBoundary>;
    $[4] = domain;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CertificatesSectionSkeleton />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== domain) {
    t6 = <SectionErrorBoundary sectionName="Certificates"><Suspense fallback={t5}><SuspendedCertificatesSection domain={domain} /></Suspense></SectionErrorBoundary>;
    $[7] = domain;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <HeadersSectionSkeleton />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== domain) {
    t8 = <SectionErrorBoundary sectionName="Headers"><Suspense fallback={t7}><SuspendedHeadersSection domain={domain} /></Suspense></SectionErrorBoundary>;
    $[10] = domain;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SeoSectionSkeleton />;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== domain) {
    t10 = <SectionErrorBoundary sectionName="SEO"><Suspense fallback={t9}><SuspendedSeoSection domain={domain} /></Suspense></SectionErrorBoundary>;
    $[13] = domain;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t2 || $[17] !== t4 || $[18] !== t6 || $[19] !== t8) {
    t11 = <>{t2}{t4}{t6}{t8}{t10}</>;
    $[15] = t10;
    $[16] = t2;
    $[17] = t4;
    $[18] = t6;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
function DomainReportSections(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="space-y-4">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function DomainReportClient(t0) {
  const $ = _c(39);
  const {
    domain
  } = t0;
  const trpc = useTRPC();
  let t1;
  if ($[0] !== domain || $[1] !== trpc.domain.getRegistration) {
    t1 = trpc.domain.getRegistration.queryOptions({
      domain
    }, staticQueryOptions);
    $[0] = domain;
    $[1] = trpc.domain.getRegistration;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: registration,
    isLoading: isRegistrationLoading,
    isError: isRegistrationError,
    error: registrationError
  } = useQuery(t1);
  const lookupFailed = Boolean(registration && !registration.success);
  const isRegistered = registration?.success === true && registration.data?.isRegistered === true;
  const isUnregistered = registration?.success === true && registration.data?.isRegistered === false;
  const {
    headerRef,
    isHeaderVisible,
    activeSection,
    scrollToSection
  } = useDomainReportTracking(domain, isRegistered);
  if (isRegistrationError || lookupFailed) {
    let t2;
    if ($[3] !== isRegistrationError || $[4] !== lookupFailed || $[5] !== registration || $[6] !== registrationError) {
      t2 = getReportErrorDescription({
        isRegistrationError,
        registrationError,
        lookupFailed,
        registration
      });
      $[3] = isRegistrationError;
      $[4] = lookupFailed;
      $[5] = registration;
      $[6] = registrationError;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== domain || $[9] !== registrationError || $[10] !== t2) {
      t3 = <DomainReportLoadError domain={domain} registrationError={registrationError} description={t2} />;
      $[8] = domain;
      $[9] = registrationError;
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  if (!isRegistrationLoading && isUnregistered) {
    let t2;
    if ($[12] !== domain) {
      t2 = <DomainUnregisteredCard domain={domain} />;
      $[12] = domain;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    return t2;
  }
  const t2 = registration?.data?.domainId;
  let t3;
  if ($[14] !== domain || $[15] !== headerRef || $[16] !== isRegistered || $[17] !== t2) {
    t3 = <DomainReportHeader domain={domain} domainId={t2} isRegistered={isRegistered} ref={headerRef} />;
    $[14] = domain;
    $[15] = headerRef;
    $[16] = isRegistered;
    $[17] = t2;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = Object.values(sections);
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== activeSection || $[21] !== domain || $[22] !== isHeaderVisible || $[23] !== scrollToSection) {
    t5 = <SectionNav domain={domain} sections={t4} activeSection={activeSection} isHeaderVisible={isHeaderVisible} onSectionClick={scrollToSection} />;
    $[20] = activeSection;
    $[21] = domain;
    $[22] = isHeaderVisible;
    $[23] = scrollToSection;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== domain || $[26] !== isRegistrationLoading || $[27] !== registration?.data) {
    t6 = isRegistrationLoading ? <RegistrationSectionSkeleton /> : <RegistrationSection domain={domain} data={registration?.data} />;
    $[25] = domain;
    $[26] = isRegistrationLoading;
    $[27] = registration?.data;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== domain || $[30] !== isRegistered) {
    t7 = isRegistered ? <RegisteredReportSections domain={domain} /> : <AllSkeletonsExceptRegistration />;
    $[29] = domain;
    $[30] = isRegistered;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== t6 || $[33] !== t7) {
    t8 = <DomainReportSections>{t6}{t7}</DomainReportSections>;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t3 || $[36] !== t5 || $[37] !== t8) {
    t9 = <>{t3}{t5}{t8}</>;
    $[35] = t3;
    $[36] = t5;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  return t9;
}